import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, Shield, Mail, ArrowRight } from 'lucide-react';

const Legal = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#F9F9FB]" data-testid="legal-page">
      {/* Navigation */}
      <nav className="border-b border-stone-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-playfair font-bold text-primary tracking-tight">
            Sharedequal
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/shipping-explained" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
              Shipping
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm">Dashboard</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm">Login</Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-6 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-playfair font-bold text-primary tracking-tight mb-4">
            Legal Information
          </h1>
          <p className="text-lg text-muted-foreground">
            Important legal documents governing your use of Sharedequal services
          </p>
        </div>
      </section>

      {/* Documents Grid */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Terms & Conditions */}
            <Card className="border-stone-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Terms & Conditions</CardTitle>
                <CardDescription>
                  Our terms of service, including drop participation rules, payment structure, and liability limitations.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/terms-and-conditions">
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm" data-testid="view-terms-button">
                    Read Terms & Conditions
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Privacy Policy */}
            <Card className="border-stone-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Privacy Policy</CardTitle>
                <CardDescription>
                  How we collect, use, and protect your personal data in compliance with Singapore's PDPA.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/privacy-policy">
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm" data-testid="view-privacy-button">
                    Read Privacy Policy
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Summary Cards */}
          <div className="mt-12 grid gap-6">
            <Card className="border-stone-200 bg-white">
              <CardHeader>
                <CardTitle className="text-lg">Key Points Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-primary mb-2">Our Service</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• We coordinate group orders to share shipping costs</li>
                      <li>• We do not manufacture or alter products</li>
                      <li>• All products come directly from brands</li>
                      <li>• Shipping is allocated fairly based on items</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-2">Your Rights</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Access your personal data</li>
                      <li>• Request corrections to your data</li>
                      <li>• Withdraw from open drops</li>
                      <li>• Opt out of marketing communications</li>
                    </ul>
                  </div>
                </div>
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <h4 className="font-semibold text-primary mb-2">Payment Structure</h4>
                  <p className="text-sm text-muted-foreground">
                    50% deposit when drop is ordered. Balance (including shipping and fees) upon ready for collection.
                    Failure to pay may result in deposit forfeiture.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card className="border-stone-200 bg-primary/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Governing Law</h4>
                    <p className="text-sm text-muted-foreground">
                      These Terms and our services are governed by the laws of the Republic of Singapore.
                      Any disputes shall be subject to the exclusive jurisdiction of the Singapore courts.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Section */}
          <div className="mt-12 text-center">
            <h3 className="text-xl font-semibold text-primary mb-4">Questions?</h3>
            <p className="text-muted-foreground mb-4">
              If you have any questions about our legal documents or policies, please contact us.
            </p>
            <a href="mailto:sharedequal@gmail.com">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/5 rounded-sm">
                <Mail className="w-4 h-4 mr-2" />
                sharedequal@gmail.com
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-stone-200 bg-white mt-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">© 2026 Sharedequal. All rights reserved.</p>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link to="/terms-and-conditions" className="hover:text-primary">Terms & Conditions</Link>
            <Link to="/privacy-policy" className="hover:text-primary">Privacy Policy</Link>
            <a href="mailto:sharedequal@gmail.com" className="hover:text-primary">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Legal;
